import { BLOCKS } from "@/blocks/registry";
import { MediaField } from "@/components/editor/MediaPicker";
import { ESArtworkField } from "@/components/editor/ESArtworkField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ChevronUp, ChevronDown, Info } from "lucide-react";

function FieldInput({ field, value, onChange }) {
  const common = "font-editor";
  if (field.type === "textarea") {
    return <Textarea data-testid={`field-${field.key}`} className={common} value={value || ""} maxLength={field.max}
                     onChange={(e) => onChange(e.target.value)} rows={4} />;
  }
  if (field.type === "boolean") {
    return (
      <div className="flex items-center gap-3 pt-1">
        <Switch data-testid={`field-${field.key}`} checked={!!value} onCheckedChange={onChange} />
        <span className="text-sm text-[#5C635C]">{value ? "מופעל" : "כבוי"}</span>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <select data-testid={`field-${field.key}`} value={value || field.default} onChange={(e) => onChange(e.target.value)}
              className="font-editor h-10 rounded-md border border-[#E5E2D9] bg-white px-3 text-sm w-full">
        {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === "media") {
    return <MediaField value={value} onChange={onChange} help={field.help} />;
  }
  if (field.type === "artwork") {
    return <ESArtworkField value={value} onChange={onChange} help={field.help} />;
  }
  return <Input data-testid={`field-${field.key}`} className={common} value={value || ""} maxLength={field.max}
                placeholder={field.type === "url" ? "https://..." : ""} onChange={(e) => onChange(e.target.value)} />;
}

function Repeater({ field, value, onChange }) {
  const items = value || [];
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const add = () => onChange([...items, {}]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-[#E5E2D9] rounded-xl p-4 bg-[#FBFAF7]" data-testid={`repeater-${field.key}-item-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-[#4A5D4E]">{field.itemLabel} {i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-[#F7F5F0] text-[#5C635C]"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-[#F7F5F0] text-[#5C635C]"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`repeater-${field.key}-remove-${i}`} className="p-1.5 rounded hover:bg-red-50 text-[#8C3A3A]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="space-y-3">
            {field.item_fields.map((sf) => (
              <div key={sf.key}>
                <label className="block text-xs font-medium text-[#5C635C] mb-1">
                  {sf.label}{sf.required && <span className="text-[#8C3A3A]"> *</span>}
                </label>
                <FieldInput field={sf} value={it[sf.key]} onChange={(v) => update(i, sf.key, v)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`repeater-${field.key}-add`}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#E5E2D9] rounded-xl py-3 text-[#4A5D4E] hover:border-[#4A5D4E] transition-colors">
        <Plus className="w-4 h-4" /> הוספת {field.itemLabel}
      </button>
    </div>
  );
}

export default function BlockForm({ block, onChange }) {
  const def = BLOCKS[block.type];
  if (!def) return null;
  const setData = (key, val) => onChange({ ...block, data: { ...block.data, [key]: val } });

  return (
    <div className="space-y-5">
      <p className="flex items-start gap-2 text-sm text-[#5C635C] bg-[#F2F5F1] rounded-lg p-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#4A5D4E]" /> {def.description}
      </p>
      {def.fields.map((f) => (
        <div key={f.key}>
          <label className="block text-sm font-semibold text-[#1A1D1A] mb-1.5">
            {f.label}{f.required && <span className="text-[#8C3A3A]"> *</span>}
          </label>
          {f.type === "repeater" ? (
            <Repeater field={f} value={block.data[f.key]} onChange={(v) => setData(f.key, v)} />
          ) : (
            <FieldInput field={f} value={block.data[f.key]} onChange={(v) => setData(f.key, v)} />
          )}
          {f.help && f.type !== "media" && f.type !== "artwork" && <p className="text-xs text-[#8a8f89] mt-1.5">{f.help}</p>}
        </div>
      ))}
    </div>
  );
}
